import { useState, useEffect, useRef, useMemo } from "react";
import AppIcon from "@/components/AppIcon";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, ListChecks, BookOpen, Sparkles, Star, Camera, X, Lock, HelpCircle, Loader2, Hammer, ChevronDown, ChevronUp, ImagePlus, Mic, Target, Globe, Check } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import WhatIsModal from "@/components/WhatIsModal";
import GoalGuideModal from "@/components/GoalGuideModal";
import StaircaseLoader from "@/components/StaircaseLoader";
import FreeSampleModal, { FREE_SAMPLE_DISMISS_KEY } from "@/components/FreeSampleModal";
import CreatorModeCards from "@/components/CreatorModeCards";
import CreatorModeBrochures from "@/components/CreatorModeBrochures";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WHATS_NEXT = [
  [5, "Preview Before Publish"], [6, "Digital Downloads"], [7, "Thumbnail"], [8, "Voice Over"],
  [9, "Music"], [10, "Text & Language"], [11, "Amazon Products"], [12, "Retention Card"],
  [13, "Video Clips"], [14, "Video Quiz"], [15, "End Card"], [16, "Finalize & Render"],
];

const WhatsNextList = () => (
  <div className="text-left md:border-l md:border-white/15 md:pl-10" data-testid="whats-next-list">
    <h2 className="text-lg font-extrabold text-white mb-3">What's Next?</h2>
    <ul className="space-y-1.5">
      {WHATS_NEXT.map(([n, label], i) => (
        <li key={n} className="grid grid-cols-[4.5rem_1fr] text-sm text-white sms-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`whats-next-step-${n}`}>
          <span className="font-bold text-white/70">Step {n}</span>
          <span>{label}</span>
        </li>
      ))}
    </ul>
  </div>
);


const SKILLS = [
  { id: "beginner", label: "Novice", desc: "Plain & simple, safety-first" },
  { id: "handy", label: "Handy", desc: "Some know-how assumed" },
  { id: "pro", label: "Pro", desc: "Full technical detail" },
];

const EXAMPLES = [
  "Who was Marcus Aurelius?",
  "What's it like in New Zealand?",
  "Create a kids story about a bird and a dog",
  "What does it take to be a Senator?",
  "Does anyone know who built the Pyramids?",
  "How to make a slow-cook pot roast",
  "What is the best way to raise self-esteem in my child?",
  "What breed of dog doesn't bark a lot?",
  "Three yoga poses I should do every day?",
  "What does it mean to live in the moment?",
];

const LOCATION_PLACEHOLDERS = [
  "Beachside mid-century home, large windows, view of the ocean, minimal furniture, modern art, exotic plants, warm lighting",
  "Chef's kitchen. Large stainless steel restaurant appliances. Industrial lighting. Open flame pizza oven. very clean and professional",
  "Atop a large mountain in Yosemite. Deep blue skies and puffy clouds. Tall pines and majestic mountains. Lake in the distance.",
];

// Selection-driven "Create Your Video Location" — a few taps establish 80-90% of the scene,
// then the AI expands the choices into a full scene prompt. Free text is optional at the end.
const SETTING_TYPES = [
  ["home", "Home"], ["business", "Business / Office"], ["workshop", "Workshop / Garage"],
  ["restaurant", "Restaurant / Kitchen"], ["school", "School / Classroom"], ["medical", "Medical / Clinical"],
  ["nature", "Nature / Wilderness"], ["urban", "Urban / Street"], ["farm", "Farm / Rural"],
  ["photo_studio", "Photo Session / Studio"], ["other", "Other"],
];
const SPECIFIC_SPACES = {
  home: [["kitchen", "Kitchen"], ["living_room", "Living room"], ["bedroom", "Bedroom"], ["bathroom", "Bathroom"], ["garage", "Garage"], ["driveway", "Driveway"], ["backyard", "Backyard"]],
  business: [["office", "Office"], ["store", "Store"], ["warehouse", "Warehouse"], ["factory", "Factory"], ["studio", "Studio"], ["conference", "Conference room"]],
  workshop: [["workbench", "Workbench"], ["garage_bay", "Garage bay"], ["machine_shop", "Machine shop"], ["tool_wall", "Tool wall"]],
  restaurant: [["dining", "Dining room"], ["kitchen", "Kitchen"], ["bar", "Bar"], ["patio", "Patio"]],
  school: [["classroom", "Classroom"], ["lecture", "Lecture hall"], ["lab", "Lab"], ["library", "Library"]],
  medical: [["exam", "Exam room"], ["operating", "Operating room"], ["waiting", "Waiting room"], ["lab", "Lab"]],
  nature: [["forest", "Forest"], ["riverbank", "Riverbank"], ["meadow", "Meadow"], ["mountainside", "Mountainside"], ["beach", "Beach"]],
  urban: [["sidewalk", "Sidewalk"], ["alley", "Alley"], ["rooftop", "Rooftop"], ["plaza", "Plaza"], ["storefront", "Storefront"]],
  farm: [["barn", "Barn"], ["field", "Field"], ["stable", "Stable"], ["orchard", "Orchard"], ["greenhouse", "Greenhouse"]],
  photo_studio: [["seamless", "Seamless backdrop"], ["cyclorama", "Cyclorama / infinity wall"], ["product_table", "Product table"], ["beauty_set", "Beauty / portrait set"], ["auto_studio", "Automotive studio"], ["dark_studio", "Dark dramatic studio"]],
  other: [],
};
const SURROUNDINGS = [["city", "City"], ["suburban", "Suburban"], ["rural", "Rural"], ["coastal", "Coastal"], ["mountain", "Mountain"], ["desert", "Desert"], ["forest", "Forest"], ["tropical", "Tropical"]];
const WEATHER = [["clear", "Clear"], ["sunny", "Sunny"], ["cloudy", "Overcast"], ["raining", "Raining"], ["snow", "Snow"], ["foggy", "Foggy"], ["stormy", "Stormy"], ["windy", "Windy"]];
const TIMES = [["", "Any"], ["morning", "Morning"], ["day", "Day"], ["golden", "Golden hour"], ["evening", "Evening"], ["night", "Night"]];
// [id, short label, rich description fed to the AI so users don't have to describe the look themselves]
const LIGHTING = [
  ["sunlight", "Natural Sunlight", "bright, realistic natural daylight with natural shadows"],
  ["soft", "Soft Natural Light", "soft, diffused window light — gentle and flattering"],
  ["golden", "Warm Golden Light", "warm, inviting golden-hour tones"],
  ["airy", "Bright & Airy", "bright, high-key lighting with minimal shadows — clean and upbeat"],
  ["cool", "Cool Modern Light", "crisp, slightly cool illumination — clean and modern for tech, automotive or medical looks"],
  ["cinematic", "Cinematic Lighting", "dramatic cinematic lighting with bold highlights and shadows and a movie-like feel"],
  ["moody", "Low-Key / Moody Lighting", "low-key, moody lighting — darker with strong contrast and selective illumination"],
  ["neon", "Neon / Colored Lighting", "vibrant neon and colored lighting with a nightlife atmosphere"],
  ["photosession", "Photo Session", "a slick, high-end studio photo-session look: a dark, dramatic studio with creative background lighting effects — warm glowing vertical LED light bars and soft accent beams streaking through the shadows — and a glossy wet-look reflective black floor that mirrors the subject. The subject is polished to a showroom shine (waxed and buffed) with crisp specular highlights and reflections rolling across its surface, cinematic rim lighting separating it from the deep-black background, rich contrast and inky blacks, shot photorealistically from a flattering low camera angle close to the ground for a sexy, premium automotive-commercial / magazine-cover feel"],
];
const _labelOf = (list, val) => (list.find(([v]) => v === val)?.[1] || "");


export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh, user } = useAuth();
  const [goal, setGoal] = useState("");
  const [rough, setRough] = useState("");
  const [crafting, setCrafting] = useState(false);
  const [qBusy, setQBusy] = useState(false);
  const [questions, setQuestions] = useState(null);   // [{id,q,options,multi,allow_other}] or null
  const [answers, setAnswers] = useState({});          // { [qId]: { picks: string[], other: string } }
  const [otherOpen, setOtherOpen] = useState({});      // { [qId]: bool }
  const [guidedQA, setGuidedQA] = useState([]);        // persisted [{q,a}] from the last guided submit
  const [siteUrl, setSiteUrl] = useState("");
  const [siteBusy, setSiteBusy] = useState(false);
  const [siteBrand, setSiteBrand] = useState(null);    // { business, brief, brand_colors[], logo_url, images[], source_url, thin, note }

  // "Add your website for more detail": scrape the public page → grounded brief + brand kit, and
  // STORE it (don't touch the prompt). It gets layered in only when the user taps "Craft my request".
  const buildFromWebsite = async () => {
    const u = siteUrl.trim();
    if (u.length < 4) { toast.error("Enter your website address first"); return; }
    setSiteBusy(true);
    try {
      const { data } = await api.post("/scrape-site", { url: u });
      setSiteBrand(data);
      if (data.note) toast.message(data.note);
      else toast.success(`Added ${data.business || "your site"} — we'll weave it in when you craft your request`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't read that website — check the address and that it's public");
    } finally {
      setSiteBusy(false);
    }
  };

  // STEP ONE trigger: fetch smart questions first; if none come back, craft straight away.
  const craftPrompt = async () => {
    const text = rough.trim();
    if (text.length < 2) { toast.error("Type your idea first"); return; }
    setQBusy(true);
    setQuestions(null);
    try {
      const { data } = await api.post("/guided-questions", { text });
      const qs = data?.questions || [];
      if (qs.length) {
        setAnswers({});
        setOtherOpen({});
        setQuestions(qs);
      } else {
        await enhance(text, null);  // graceful fallback — no useful questions
      }
    } catch (e) {
      await enhance(text, null);
    } finally {
      setQBusy(false);
    }
  };

  // Toggle a tapped option (single- or multi-select) for a question.
  const togglePick = (q, opt) => {
    setAnswers((prev) => {
      const cur = prev[q.id]?.picks || [];
      let picks;
      if (q.multi) picks = cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt];
      else picks = cur.includes(opt) ? [] : [opt];
      return { ...prev, [q.id]: { ...(prev[q.id] || {}), picks } };
    });
  };
  const setOther = (qId, val) => setAnswers((prev) => ({ ...prev, [qId]: { ...(prev[qId] || {}), other: val } }));

  // Build the {q,a} list from the tapped answers and craft the targeted prompt.
  const submitAnswers = async () => {
    const qa = (questions || []).map((q) => {
      const a = answers[q.id] || {};
      const parts = [...(a.picks || [])];
      if ((a.other || "").trim()) parts.push(a.other.trim());
      return { q: q.q, a: parts.join(", ") };
    }).filter((x) => x.a);
    setQuestions(null);
    setGuidedQA(qa);
    await enhance(rough.trim(), qa.length ? qa : null);
  };

  const skipQuestions = async () => {
    setQuestions(null);
    await enhance(rough.trim(), null);
  };

  const enhance = async (text, qa) => {
    setCrafting(true);
    try {
      const website = siteBrand?.brief ? { business: siteBrand.business || "", brief: siteBrand.brief } : undefined;
      const payload = { text };
      if (qa) payload.answers = qa;
      if (website) payload.website = website;
      const { data } = await api.post("/enhance-prompt", payload);
      if (data?.prompt) {
        setGoal(data.prompt);
        toast.success(qa ? "Locked in — here's your targeted request ✨" : "Here's a sharper request — tweak it if you like ✨");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't craft a prompt right now — please try again.");
    } finally {
      setCrafting(false);
    }
  };
  const level = "detailed";  // single product — always a full 15-step video
  const [skill, setSkill] = useState(() => {
    try { return localStorage.getItem("sms_skill_level") || "beginner"; } catch { return "beginner"; }
  });
  const chooseSkill = (id) => {
    setSkill(id);
    try { localStorage.setItem("sms_skill_level", id); } catch { /* ignore */ }
    // sync to the signed-in user's account so it follows them across devices
    if (user) api.put("/settings", { skill_level: id }).then(() => refresh()).catch(() => {});
  };
  const [busy, setBusy] = useState(false);
  // Collapsible STEP ONE/TWO/THREE cards. Default CLOSED for a clean first look; they reset to closed
  // every time the generator is freshly opened, but keep whatever the user changes during that session.
  const [s1Open, setS1Open] = useState(false);
  const [s2Open, setS2Open] = useState(false);
  const [s3Open, setS3Open] = useState(false);
  const toggleStep = (setter) => setter((v) => !v);
  const [genProgress, setGenProgress] = useState(0);
  const [genEta, setGenEta] = useState(null);
  const genStartRef = useRef(0);
  const genEstRef = useRef(0);
  const [showWhatIs, setShowWhatIs] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);

  // Every fresh open of the generator starts with all steps collapsed (clean look); toggles made
  // while it's open stick until it's closed and reopened.
  useEffect(() => {
    if (showGenerator) { setS1Open(false); setS2Open(false); setS3Open(false); }
  }, [showGenerator]);

  useEffect(() => {
    let flagged = false;
    try { flagged = sessionStorage.getItem("sms_open_generator") === "1"; } catch (e) {}
    if (flagged) {
      try { sessionStorage.removeItem("sms_open_generator"); } catch (e) {}
      setShowGenerator(true);
      setTimeout(() => document.querySelector('[data-testid="prompt-crafter"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, []);
  const [showGoalGuide, setShowGoalGuide] = useState(false);
  const [exampleIdx, setExampleIdx] = useState(0);
  const [focused, setFocused] = useState(false);
  const [msPhoto, setMsPhoto] = useState(null);
  const [msOutfit, setMsOutfit] = useState("");
  const [msAvailable, setMsAvailable] = useState(true);
  const [freeSampleEnabled, setFreeSampleEnabled] = useState(false);
  const [showFreeSample, setShowFreeSample] = useState(false);
  // Look controls (chosen up front so photos generate exactly once — replaces the old restyle)
  const [peopleCount, setPeopleCount] = useState(1);      // 1 = single host, 2 = two people
  const [peopleShots, setPeopleShots] = useState("all");  // "all" | "10" | "5" | "none" (objects only)
  const [hostGender, setHostGender] = useState("");   // "" | "man" | "woman"
  const [castHint, setCastHint] = useState("");
  const [language, setLanguage] = useState("en");
  const [tone, setTone] = useState("happy");
  const [teachStyle, setTeachStyle] = useState("howto");
  const [artStyle, setArtStyle] = useState("photo");   // "photo" (realistic) | "illustration" (soft storybook art)
  const [imageTemplate, setImageTemplate] = useState(null);  // data URL of an uploaded character reference
  const [introOpen, setIntroOpen] = useState(() => { try { return localStorage.getItem("sms_intro_open") !== "0"; } catch (e) { return true; } });
  const onTemplatePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Please use an image under 12MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setImageTemplate(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const [audience, setAudience] = useState(() => {
    try { return localStorage.getItem("sms_audience") || "everyone"; } catch { return "everyone"; }
  });
  const pickAudience = (id) => {
    setAudience(id);
    try { localStorage.setItem("sms_audience", id); } catch { /* ignore */ }
  };
  const [timeOfDay, setTimeOfDay] = useState("");      // "" | morning | day | golden | evening | night
  const [placeType, setPlaceType] = useState("");      // "" | "indoor" | "outdoor" | "both"
  const [settingType, setSettingType] = useState("");  // Home / Business / Workshop / ...
  const [specificSpace, setSpecificSpace] = useState(""); // dynamic, based on settingType
  const [surroundings, setSurroundings] = useState(""); // City / Suburban / Coastal / ...
  const [weather, setWeather] = useState("");          // Raining / Snow / Foggy / ...
  const [lighting, setLighting] = useState("");        // sunlight / soft / golden / cinematic / moody / neon
  const [sceneHint, setSceneHint] = useState("");
  const [locationHint, setLocationHint] = useState("");
  const [currentEvents, setCurrentEvents] = useState("");  // News style: preferred outlet(s) for live Tavily sourcing
  const [locPh, setLocPh] = useState(0);
  const msEnabled = !!(msAvailable && user?.moviestar_active && user?.settings?.moviestar_enabled);

  // Compose the tap-selected location markers + free text into one rich scene hint for the AI.
  // Shared by the live preview below and the submit payload so they can never drift apart.
  const composedScene = useMemo(() => {
    const setLbl = _labelOf(SETTING_TYPES, settingType);
    const spaceLbl = _labelOf(SPECIFIC_SPACES[settingType] || [], specificSpace);
    const surrLbl = _labelOf(SURROUNDINGS, surroundings);
    const wxLbl = _labelOf(WEATHER, weather);
    const bits = [];
    if (setLbl && settingType !== "other") bits.push(spaceLbl ? `${setLbl} — ${spaceLbl}` : setLbl);
    if (surrLbl) bits.push(`${surrLbl} surroundings`);
    if (wxLbl) bits.push(`${wxLbl} weather`);
    const lightItem = LIGHTING.find(([id]) => id === lighting);
    if (lightItem) bits.push(lightItem[2]);
    if (locationHint.trim()) bits.push(locationHint.trim());
    return bits.join("; ");
  }, [settingType, specificSpace, surroundings, weather, lighting, locationHint]);


  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setMsAvailable(data.moviestar_available !== false);
      setFreeSampleEnabled(data.free_sample_enabled === true);
    }).catch(() => {});
  }, []);

  // Return-after-buying: restore the goal the user typed before topping up.
  useEffect(() => {
    if (!location.state?.restoreGoal) return;
    try {
      const raw = localStorage.getItem("sms_pending_goal");
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.goal) {
          setGoal(p.goal);
          if (p.skill) setSkill(p.skill);
          toast.success("You're topped up — we brought back your goal. Ready when you are! ✨", { duration: 5000 });
        }
      }
      localStorage.removeItem("sms_pending_goal");
    } catch { /* ignore */ }
    navigate(".", { replace: true, state: {} });
  }, [location.state, navigate]);

  // Prefill from a "make your own version" / "create this gap" link: /studio?goal=…&audience=…&style=…
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let g = params.get("goal");
    let a = params.get("audience");
    let s = params.get("style");
    // Fallback: a stash left by a "Create this / make your own version" CTA (survives a login redirect that strips the query).
    if (!g && !s && !a) {
      try {
        const raw = localStorage.getItem("sms_studio_prefill");
        if (raw) { const p = JSON.parse(raw); g = p.goal || g; s = p.style || s; a = p.audience || a; }
      } catch { /* ignore */ }
    }
    try { localStorage.removeItem("sms_studio_prefill"); } catch { /* ignore */ }
    if (g) setGoal(g);
    if (a && ["everyone", "kids", "teens", "adults"].includes(a)) {
      setAudience(a);
      try { localStorage.setItem("sms_audience", a); } catch { /* ignore */ }
    }
    if (s && ["howto", "lecture", "documentary", "storytelling", "coach", "casual", "brief", "showhost", "advice", "news", "marketing"].includes(s)) {
      setTeachStyle(s);
    }
    if (g || a || s) {
      navigate(location.pathname, { replace: true });
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
      toast.success("We pre-filled your idea — tweak it and press create. ✨", { duration: 5000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const stashPendingGoal = () => {
    try {
      localStorage.setItem("sms_pending_goal", JSON.stringify({ goal: goal.trim(), level, skill }));
    } catch { /* ignore */ }
  };

  const onMsPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Please choose a photo under 8MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setMsPhoto(reader.result);
    reader.onerror = () => toast.error("Could not read that photo");
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const s = user?.settings?.skill_level;
    if (s) setSkill(s);
  }, [user?.settings?.skill_level]);

  useEffect(() => {
    if (goal.trim().length > 0) return; // pause rotation while user is typing
    const t = setInterval(() => setExampleIdx((i) => (i + 1) % EXAMPLES.length), 3000);
    return () => clearInterval(t);
  }, [goal]);
  const placeholder = EXAMPLES[exampleIdx];

  const generate = async () => {
    if (!goal.trim()) { toast.error("Tell me what you want to accomplish"); return; }
    // No free trial — new users need a credit pack before generating.
    if (!user?.unlimited && (!user?.credits || user.credits <= 0)) {
      let dismissed = false;
      try { dismissed = !!localStorage.getItem(FREE_SAMPLE_DISMISS_KEY); } catch { /* ignore */ }
      // Offer the one-time free sample (pre-filled with what they typed) before the paywall.
      if (freeSampleEnabled && user && !user.free_sample_used && !dismissed) {
        setShowFreeSample(true);
        return;
      }
      stashPendingGoal();
      toast("Add funds to start creating — $3.99 a video, everything included", { icon: "✨" });
      navigate("/pricing");
      return;
    }
    setBusy(true);
    setGenProgress(0);
    genStartRef.current = Date.now();
    genEstRef.current = 100;
    setGenEta(genEstRef.current);
    try {
      const { data } = await api.post("/manuals", {
        goal: goal.trim(),
        detail_level: level,
        skill_level: skill,
        ...(peopleShots === "none"
          ? { no_people: true }
          : {
              ...(peopleCount === 2
                ? { two_people: true, ...(castHint.trim() ? { cast_hint: castHint.trim() } : {}) }
                : { host: true, ...(hostGender ? { host_gender: hostGender } : {}) }),
              ...(peopleShots === "5" || peopleShots === "10"
                ? { broll: true, broll_count: 15 - Number(peopleShots) }
                : {}),
            }),
        ...(language && language !== "en" ? { language } : {}),
        ...(tone && tone !== "happy" ? { tone } : {}),
        ...(teachStyle && teachStyle !== "howto" ? { style: teachStyle } : {}),
        ...(artStyle === "illustration" ? { art_style: "illustration" } : {}),
        ...(imageTemplate ? { image_template: imageTemplate } : {}),
        ...(audience && audience !== "everyone" ? { audience } : {}),
        ...(timeOfDay ? { time_of_day: timeOfDay } : {}),
        ...(placeType ? { location: placeType } : {}),
        ...(composedScene ? { location_hint: composedScene } : {}),
        ...(sceneHint.trim() ? { scene_hint: sceneHint.trim() } : {}),
        ...(msEnabled && msPhoto ? { reference_image: msPhoto, ...(msOutfit.trim() ? { outfit: msOutfit.trim() } : {}) } : {}),
        ...(guidedQA.length ? { guided_answers: guidedQA } : {}),
        ...(siteBrand?.source_url ? { website_url: siteBrand.source_url } : {}),
        ...(siteBrand?.brand_colors?.length ? { brand_colors: siteBrand.brand_colors } : {}),
        ...(siteBrand?.logo_url ? { brand_logo_url: siteBrand.logo_url } : {}),
        ...(teachStyle === "news" && currentEvents.trim() ? { current_events: currentEvents.trim() } : {}),
      });
      const id = data.id;
      for (let i = 0; i < 160; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const { data: st } = await api.get(`/manuals/${id}/status`);
          const p = st.progress ?? 0;
          setGenProgress(p);
          // NOTE: the ETA is a fixed 100s countdown (see the interval below) — intentionally NOT
          // driven by render progress, so it never jumps around or stalls on "1s left".
          if (st.status === "ready") { await refresh(); navigate(`/manual/${id}`); return; }
          if (st.status === "error") { toast.error(st.error || "Could not generate steps. Try again.", { duration: 6000 }); setBusy(false); return; }
        } catch { /* keep polling */ }
      }
      toast.error("This is taking longer than usual — check your History in a moment.");
      setBusy(false);
    } catch (err) {
      if (err?.response?.status === 402) { stashPendingGoal(); toast.error(err.response.data.detail); navigate("/pricing"); }
      else if (err?.response?.status === 422) { toast.error(err.response.data.detail, { duration: 6000 }); setBusy(false); }
      else { toast.error(err?.response?.data?.detail || "Could not generate steps. Try again."); setBusy(false); }
    }
  };

  useEffect(() => {
    const iv = setInterval(() => setLocPh((i) => (i + 1) % LOCATION_PLACEHOLDERS.length), 7000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!busy) return;
    const iv = setInterval(() => setGenEta((e) => (e == null ? e : Math.max(1, e - 1))), 1000);
    return () => clearInterval(iv);
  }, [busy]);

  if (busy) {
    return (
      <div className="home-dark" style={{ background: "#000", minHeight: "calc(100vh - 64px)" }}>
        <div className="max-w-4xl mx-auto px-4 pt-10 pb-20">
          <div className="sms-tweed rounded-2xl border-2 p-8 sm:p-10 flex flex-col items-center text-center sms-fade-up" style={{ borderColor: "#5d8a52" }} data-testid="creating-manual-card">
            <h1 className="text-2xl md:text-3xl font-extrabold mb-1 text-white sms-outline-dark">Customizing Your Video…</h1>
            <p className="text-white mb-8">Vittyyo is customizing your video. Give us a moment.<br />Please keep this tab open.</p>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div className="flex flex-col items-center">
                <StaircaseLoader progress={genProgress} white label={genProgress < 60 ? "Writing your steps…" : genProgress < 92 ? "Creating your cover photo…" : "Finishing up…"} />
                <p className="mt-4 text-sm font-semibold text-white" data-testid="gen-eta">
                  {genEta == null ? "Estimating time…" : `About ${genEta}s left`}
                </p>
              </div>
              <WhatsNextList />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-dark" style={{ background: "#000", minHeight: "calc(100vh - 64px)" }}>
      <style>{`
        .home-dark{ color:#e9e9f1; }
        .home-dark .sms-card{ background:#0f0f16 !important; border:2px solid #5d8a52 !important; color:#fff !important; box-shadow:none !important; }
        .home-dark label{ color:#fff !important; }
        .home-dark .sms-input{ background:#000 !important; color:#fff !important; border:2px solid #5d8a52 !important; }
        .home-dark .sms-input::placeholder{ color:rgba(255,255,255,.45) !important; }
        .home-dark textarea::placeholder, .home-dark input::placeholder{ color:rgba(255,255,255,.4) !important; }
        .home-dark .bg-white{ background:#0f0f16 !important; }
        .home-dark .bg-white\\/70{ background:rgba(255,255,255,.08) !important; }
        .home-dark select option{ background:#0f0f16; color:#fff; }
      `}</style>
      <div className="max-w-2xl mx-auto px-4 pt-4 pb-20">
      <div className="flex flex-col items-center mb-4 sms-fade-up">
        <Logo size="lg" row />
        <p className="mt-4 w-full max-w-2xl text-center text-sm md:text-base leading-snug text-white" data-testid="home-intro">
          Welcome to Vittyyo—your place to create, learn, and earn. Turn your knowledge, passions, and ideas into engaging content quickly and easily—no cameras, editing expertise, or complicated tools required. Choose from one of the three Video Creator Styles below and discover just how easy—and fun—creating your next video can be. Explore our Creator Community and share your passion.
        </p>
        <button
          type="button"
          data-testid="whatis-open-btn"
          onClick={() => setShowWhatIs(true)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white px-4 py-1.5 rounded-full transition-transform hover:-translate-y-0.5"
          style={{ background: "#1e3aaa", boxShadow: "0 8px 18px -6px rgba(30,58,170,.55)" }}
        >
          <Sparkles size={15} /> New here? See how it works!
        </button>
      </div>

      <div className="sms-fade-up" style={{ animationDelay: ".02s" }}>
        {showGenerator ? (
          <CreatorModeCards
            active="generate"
            generatorOpen={showGenerator}
            onToggleGenerator={() => setShowGenerator((v) => !v)}
          />
        ) : (
          <CreatorModeBrochures
            onChooseGenerate={() => {
              setShowGenerator(true);
              setTimeout(() => document.querySelector('[data-testid="prompt-crafter"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
            }}
          />
        )}
      </div>

      {showGenerator && (
      <>
      <div className="sms-fade-up mb-4" style={{ animationDelay: ".03s" }} data-testid="prompt-crafter">
        <div className="sms-tweed rounded-2xl border-2 p-4" style={{ borderColor: "#5d8a52" }}>
          <div className="-mx-4 -mt-4 mb-3 py-2.5 rounded-t-2xl relative flex items-center justify-center" style={{ background: "#1e3aaa" }} data-testid="step-one-stripe">
            <span data-testid="step-one-title" className="font-extrabold text-xl tracking-wide text-white">STEP ONE</span>
            <button type="button" data-testid="step-one-collapse" onClick={() => toggleStep(setS1Open, "sms_step1_open")} className="absolute right-3 inline-flex items-center gap-1 text-xs font-bold text-white/85 hover:text-white transition-colors">
              {s1Open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}<span className="hidden sm:inline">{s1Open ? "Close" : "Open"}</span>
            </button>
          </div>
          {s1Open && (<>
          <div data-testid="opening-statement" className="text-sm leading-relaxed text-white text-left space-y-2 mb-3">
            {introOpen && (
              <>
                <p>
                  What are you passionate about? What would you like to learn—or teach? With Vittyyo, the sky is the limit. Explore a world of ideas, from home improvement to history, cartoons to culinary, recipes to philosophy, and even original stories for adults and kids. Educate yourself on a myriad of topics, discover something new, or turn your own knowledge, imagination and experience into a series that teaches and inspires others.
                </p>
                <p>
                  <span className="font-extrabold text-[#8fbf82]">LEARN</span> something you've always wanted to understand. <span className="font-extrabold text-[#8fbf82]">CREATE</span> your own step-by-step guides, educational series, stories and digital content around the things you're passionate about. <span className="font-extrabold text-[#8fbf82]">EARN</span> by selling your digital downloads—and even add your own Amazon Affiliate ID to earn commissions from qualifying products you recommend.
                </p>
                <p>
                  Whether you're here to find an answer, master a new skill, tell a story, teach what you know, build a library of content, or turn your creativity into income, Vittyyo is here to help you <span className="font-extrabold text-[#8fbf82]">CREATE. LEARN. EARN.</span>
                </p>
              </>
            )}
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                data-testid="intro-toggle"
                onClick={() => setIntroOpen((v) => { const n = !v; try { localStorage.setItem("sms_intro_open", n ? "1" : "0"); } catch (e) {} return n; })}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#5d8a52] hover:text-[#3f6238] hover:underline underline-offset-2 transition-colors"
              >
                {introOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {introOpen ? "Close Intro" : "Open Intro"}
              </button>
            </div>
            <p className="font-bold text-center">So, what would you like to create today?</p>
          </div>
          <p className="text-center text-sm text-white mb-2">
            Type your basic request, curiosity, or what you'd like to learn below, then let Vittyyo craft a prompt to get a richer, more informative result.
          </p>
          <div className="text-center mb-2.5">
            <button
              type="button"
              data-testid="goal-guide-open-btn"
              onClick={() => setShowGoalGuide(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5d8a52] hover:text-[#3f6238] hover:underline underline-offset-2 transition-colors"
            >
              <HelpCircle size={15} /> What do I say? Click for guidance
            </button>
          </div>
          <div className="flex justify-end mb-1">
            <span className="text-[11px] font-semibold text-[#9a96ac]" data-testid="rough-charcount">{rough.length}/1000</span>
          </div>
          <textarea
            data-testid="rough-input"
            rows={4}
            value={rough}
            onChange={(e) => setRough(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); craftPrompt(); } }}
            placeholder={placeholder}
            maxLength={1000}
            className="sms-input w-full px-5 py-3 text-base resize-none leading-snug min-h-[120px] md:min-h-[140px]"
            style={{ border: "2px solid #1e3aaa" }}
          />
          <button
            type="button"
            data-testid="craft-prompt-btn"
            onClick={craftPrompt}
            disabled={qBusy || crafting || !rough.trim()}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-xl transition-transform hover:-translate-y-0.5 text-white disabled:opacity-60 disabled:hover:translate-y-0"
            style={{ background: "linear-gradient(160deg,#ef4444,#dc2626)", boxShadow: "0 8px 18px -6px rgba(220,38,38,.5)" }}
          >
            {qBusy ? (<><Loader2 size={17} className="spin" /> Reading your idea…</>) : crafting ? (<><Loader2 size={17} className="spin" /> Crafting your request…</>) : (<><Sparkles size={17} /> Craft my request</>)}
          </button>

          {/* Add your website for more detail — layers real site content onto the request above */}
          <div className="mt-5" data-testid="website-build-section">
            <div className="flex items-center gap-3 mb-2.5">
              <div className="flex-1 h-px bg-[#2a2735]" />
              <span className="text-xs font-extrabold text-[#9a96ac] tracking-wide">+ ADD MORE DETAIL</span>
              <div className="flex-1 h-px bg-[#2a2735]" />
            </div>
            <p className="text-sm text-white font-bold leading-snug">Add your website for more detail <span className="text-[#9a96ac] font-semibold">(optional)</span></p>
            <p className="text-[12px] text-[#9a96ac] leading-snug mt-0.5 mb-2">
              Got a website? Paste its address and we'll layer your real services, wording, photos and brand colors on top of your request above — so the video is uniquely yours, not a generic guess.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="website-url-input"
                type="text"
                inputMode="url"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buildFromWebsite(); } }}
                placeholder="yourbusiness.com"
                className="sms-input flex-1 px-4 py-3 text-base"
                style={{ border: "2px solid #5d8a52" }}
              />
              <button
                type="button"
                data-testid="website-build-btn"
                onClick={buildFromWebsite}
                disabled={siteBusy || !siteUrl.trim()}
                className="inline-flex items-center justify-center gap-2 font-bold py-3 px-5 rounded-xl text-white disabled:opacity-60 transition-transform hover:-translate-y-0.5 disabled:hover:translate-y-0"
                style={siteBrand
                  ? { background: "linear-gradient(160deg,#1e3aaa,#182e85)", boxShadow: "0 8px 18px -6px rgba(30,58,170,.5)" }
                  : { background: "linear-gradient(160deg,#4f7746,#3f6238)", boxShadow: "0 8px 18px -6px rgba(79,119,70,.5)" }}
              >
                {siteBusy ? (<><Loader2 size={17} className="spin" /> Reading your site…</>) : siteBrand ? (<><Check size={17} /> Website Added</>) : (<><Globe size={17} /> Add my site</>)}
              </button>
            </div>

            {siteBrand && (
              <div className="mt-3 rounded-2xl border-2 border-[#5d8a52] bg-[#0e1a0b] p-3.5" data-testid="website-brand-card">
                <div className="flex items-center gap-2.5">
                  {siteBrand.logo_url ? (
                    <img src={siteBrand.logo_url} alt="logo" className="w-9 h-9 rounded-lg object-contain bg-white/90 p-1 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : null}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 font-extrabold text-white text-sm truncate">
                      <Check size={14} className="text-[#4ade80] shrink-0" /> Read {siteBrand.business || "your site"}
                    </div>
                    <div className="text-[11px] text-[#9a96ac]">
                      {(siteBrand.images?.length || 0)} photo{(siteBrand.images?.length || 0) === 1 ? "" : "s"} found · brand colors pulled
                    </div>
                  </div>
                  {siteBrand.brand_colors?.length > 0 && (
                    <div className="flex gap-1 ml-auto shrink-0" data-testid="brand-swatches">
                      {siteBrand.brand_colors.slice(0, 4).map((c, i) => (
                        <span key={i} className="w-5 h-5 rounded-md border border-white/20" style={{ background: c }} title={c} />
                      ))}
                    </div>
                  )}
                </div>
                {siteBrand.images?.length > 0 && (
                  <div className="flex gap-1.5 mt-2.5 overflow-x-auto pb-1" data-testid="brand-image-strip">
                    {siteBrand.images.slice(0, 8).map((src, i) => (
                      <img key={i} src={src} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0 border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ))}
                  </div>
                )}
                {siteBrand.note && <p className="text-[11px] text-[#ffd24a] mt-2 leading-snug">{siteBrand.note}</p>}
                <p className="text-[11px] text-[#a9d79b] mt-2 leading-snug">Saved. We'll weave this into your request when you tap <span className="font-bold text-white">Craft my request</span> above.</p>
              </div>
            )}
          </div>

          {questions && questions.length > 0 && (
            <div className="mt-4 rounded-2xl border-2 border-[#1e3aaa] bg-[#101019] p-4" data-testid="guided-questions">
              <div className="text-center mb-1">
                <span className="inline-flex items-center gap-1.5 text-sm font-extrabold text-[#8fbf82]"><Target size={15} /> Let's aim your lesson</span>
              </div>
              <p className="text-center text-xs text-[#9a96ac] mb-3">Tap what fits — or skip. A few quick picks make your video far more on-target.</p>
              <div className="space-y-3">
                {questions.map((q, qi) => {
                  const picks = answers[q.id]?.picks || [];
                  return (
                    <div key={q.id} className="sms-tweed rounded-xl bg-[#0f0f16] border border-[#2a2536] p-3" data-testid={`guided-q-${qi}`}>
                      <div className="text-sm font-bold text-white mb-2">{qi + 1}. {q.q}{q.multi && <span className="ml-1 text-[10px] font-semibold text-[#9a96ac]">· pick any</span>}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {q.options.map((opt) => {
                          const on = picks.includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              data-testid={`guided-opt-${qi}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              onClick={() => togglePick(q, opt)}
                              className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                              style={on ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}
                            >
                              {on && "✓ "}{opt}
                            </button>
                          );
                        })}
                        {q.allow_other && (otherOpen[q.id] ? (
                          <input
                            autoFocus
                            data-testid={`guided-other-input-${qi}`}
                            value={answers[q.id]?.other || ""}
                            onChange={(e) => setOther(q.id, e.target.value)}
                            maxLength={60}
                            placeholder="Something else…"
                            className="sms-input text-sm px-4 py-2 rounded-full w-full basis-full"
                            style={{ border: "1.5px solid #5d8a52" }}
                          />
                        ) : (
                          <button
                            type="button"
                            data-testid={`guided-other-btn-${qi}`}
                            onClick={() => setOtherOpen((p) => ({ ...p, [q.id]: true }))}
                            className="text-xs font-bold px-3 py-1.5 rounded-full"
                            style={{ background: "#000", color: "#9a96ac", border: "1.5px dashed #5d8a52" }}
                          >
                            + Something else
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  data-testid="guided-submit-btn"
                  onClick={submitAnswers}
                  disabled={crafting}
                  className="flex-1 inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-xl text-white disabled:opacity-60"
                  style={{ background: "linear-gradient(160deg,#ef4444,#dc2626)" }}
                >
                  {crafting ? (<><Loader2 size={16} className="spin" /> Targeting…</>) : (<><Sparkles size={16} /> Submit &amp; craft</>)}
                </button>
                <button
                  type="button"
                  data-testid="guided-skip-btn"
                  onClick={skipQuestions}
                  disabled={crafting}
                  className="inline-flex items-center justify-center font-bold py-2.5 px-4 rounded-xl text-[#a9d79b] disabled:opacity-60"
                  style={{ background: "#000", border: "1.5px solid #5d8a52" }}
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-[#dbe4d3]">
            <label className="block text-center font-bold text-lg mb-3" data-testid="prompt-result-title">Vittyyo Prompt Result</label>
            <div className="flex justify-end mb-1">
              <span className="text-[11px] font-semibold text-[#9a96ac]" data-testid="goal-charcount">{goal.length}/1000</span>
            </div>
            <textarea
              data-testid="goal-input"
              rows={4}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); generate(); } }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="( Vittyyo will add your detailed request here )"
              maxLength={1000}
              className="sms-input w-full px-5 py-4 text-lg resize-none leading-snug min-h-[120px] md:min-h-[140px]"
              style={{ border: `2px solid ${focused ? "#16307f" : "#1e3aaa"}` }}
            />
          </div>

          <div className="mt-4 pt-4 border-t border-[#dbe4d3]" data-testid="skill-level-picker">
          <div className="text-center font-bold text-lg mb-3">Choose your skill level</div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {SKILLS.map(({ id, label }) => {
              const active = skill === id;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`skill-${id}`}
                  onClick={() => chooseSkill(id)}
                  aria-pressed={active}
                  className="inline-flex items-center justify-center text-sm font-bold px-4 py-1.5 rounded-full transition-colors"
                  style={active
                    ? { background: "#1e3aaa", color: "#fff" }
                    : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div data-testid="skill-level-hint" className="text-center text-sm text-[#a9b0c0] mt-2.5">
            {SKILLS.find((s) => s.id === skill)?.desc}
          </div>
          </div>
          </>)}
        </div>
      </div>

      <div className="mt-4 sms-fade-up" style={{ animationDelay: ".09s" }} data-testid="look-picker">
        <div className="sms-tweed rounded-2xl border-2 p-4" style={{ borderColor: "#5d8a52" }}>
          <div className="-mx-4 -mt-4 mb-3 py-2.5 rounded-t-2xl relative flex items-center justify-center" style={{ background: "#1e3aaa" }} data-testid="step-two-stripe">
            <span data-testid="step-two-title" className="font-extrabold text-xl tracking-wide text-white">STEP TWO</span>
            <button type="button" data-testid="step-two-collapse" onClick={() => toggleStep(setS2Open, "sms_step2_open")} className="absolute right-3 inline-flex items-center gap-1 text-xs font-bold text-white/85 hover:text-white transition-colors">
              {s2Open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}<span className="hidden sm:inline">{s2Open ? "Close" : "Open"}</span>
            </button>
          </div>
          {s2Open && (<>
          <div className="text-center font-bold text-lg mb-1">Design Your Video Style <span className="text-sm font-medium text-[#9a96ac]">(optional)</span></div>
          <p className="text-center text-xs text-[#9a96ac] mb-3">Set it here once so your photos generate a single time.</p>

          <div className="mb-3 pb-3 border-b border-[#dbe4d3]" data-testid="tone-picker">
            <div className="text-lg font-bold text-center text-white mb-2">Narrator's vibe</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {[
                ["happy", "😊 Happy", "Upbeat & optimistic"],
                ["calm", "🌿 Calm", "Relaxed but on point"],
                ["excited", "🤩 Excited", "Energetic, not annoying"],
                ["inspirational", "💪 Inspirational", "You can do this!"],
                ["peaceful", "🧘 Peaceful", "Gentle & zen"],
              ].map(([id, label, desc]) => (
                <button key={id} type="button" data-testid={`tone-${id}`} onClick={() => setTone(id)} title={desc}
                  className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={tone === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#9a96ac] mt-1.5 text-center">{[["happy", "Upbeat & optimistic"], ["calm", "Relaxed but on point"], ["excited", "Energetic, but never over-the-top"], ["inspirational", "Encouraging — \u201cyou can do this!\u201d"], ["peaceful", "Gentle, soothing & zen"]].find(([id]) => id === tone)?.[1]}</p>
          </div>

          <div className="mb-3 pb-3 border-b border-[#dbe4d3]" data-testid="style-picker">
            <div className="text-lg font-bold text-center text-white mb-2">Speaking style</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {[
                ["howto", "📋 How-To Guide", "Clear step-by-step tutorial"],
                ["lecture", "🎓 Lecture", "Structured, educational class"],
                ["documentary", "🎬 Documentary", "Cinematic, immersive narration"],
                ["storytelling", "📖 Storytelling", "Teach through a story"],
                ["coach", "🔥 Coach", "Motivating & encouraging"],
                ["casual", "☕ Casual Chat", "Relaxed, like a friend"],
                ["brief", "💼 Professional Brief", "Crisp business briefing"],
                ["showhost", "🎤 Show Host", "Lively on-camera presenter"],
                ["advice", "🧭 Advice", "Wise mentor helps you decide"],
                ["news", "📰 News", "Anchor delivering the facts"],
                ["marketing", "📣 Marketing", "Polished brand & promo narration"],
              ].map(([id, label, desc]) => (
                <button key={id} type="button" data-testid={`style-${id}`} onClick={() => setTeachStyle(id)} title={desc}
                  className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={teachStyle === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#9a96ac] mt-1.5 text-center">{[["howto", "Clear, practical step-by-step tutorial"], ["lecture", "Structured, educational class"], ["documentary", "Cinematic, immersive documentary narration"], ["storytelling", "Teach through an engaging story"], ["coach", "Motivating, confidence-building coach"], ["casual", "Relaxed, conversational — like a friend"], ["brief", "Crisp, polished professional briefing"], ["showhost", "Lively, entertaining show host"], ["advice", "A wise mentor who weighs both sides and helps you decide"], ["news", "A news anchor delivering the facts — updates, bullet points and the bottom line"], ["marketing", "Polished brand narration for a business — benefit-led promo copy, no tutorial scaffolding"]].find(([id]) => id === teachStyle)?.[1]}</p>
          </div>

          {teachStyle === "news" && (
            <div className="mt-3 pt-3 border-t border-[#dbe4d3]" data-testid="current-events-section">
              <div className="text-lg font-bold text-center text-white mb-1 flex items-center justify-center gap-2">
                <span>📰 Current Events</span>
              </div>
              <p className="text-[11px] text-[#9a96ac] mb-2 text-center leading-relaxed">
                For real, up-to-the-minute reporting we'll pull <b className="text-[#a9d79b]">actual current articles</b> on your topic and ground the report in them — with the date &amp; real sources shown on screen. Name a preferred outlet below (optional).
              </p>
              <input
                data-testid="current-events-input"
                value={currentEvents}
                onChange={(e) => setCurrentEvents(e.target.value)}
                maxLength={200}
                placeholder="e.g. Los Angeles Times, Reuters, latimes.com — optional"
                className="w-full sms-input px-3 py-2.5 text-sm rounded-xl"
              />
              <p className="text-[10px] text-[#7f9a74] mt-1.5 text-center">
                Leave blank to search across all reputable news sources. Sources are real &amp; verifiable — we never invent them.
              </p>
            </div>
          )}

          <div data-testid="audience-picker">
            <div className="text-lg font-bold text-center text-white mb-2">Reading level</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {[
                ["everyone", "👪 Everyone"],
                ["kids", "🧒 Kids"],
                ["teens", "🧑‍🎓 Teens"],
                ["adults", "🎓 Adults"],
              ].map(([id, label]) => (
                <button key={id} type="button" data-testid={`audience-${id}`} onClick={() => pickAudience(id)}
                  className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={audience === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#9a96ac] mt-1.5 text-center">{{
              everyone: "Clear language for a general audience",
              kids: "Short, simple sentences — gentle words that still stretch young readers (ages ~6–10)",
              teens: "Richer words & varied sentences that challenge teen readers (ages ~13–17)",
              adults: "Sophisticated vocabulary & complex sentences for college-educated adults",
            }[audience]}</p>
          </div>

          <div className="mt-3 pt-3 border-t border-[#dbe4d3]" data-testid="artstyle-picker">
            <div className="text-lg font-bold text-center text-white mb-2">Picture style</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {[
                ["photo", "📷 Photo", "Realistic photos"],
                ["illustration", "🎨 Illustration", "Soft hand-drawn storybook art"],
              ].map(([id, label, desc]) => (
                <button key={id} type="button" data-testid={`artstyle-${id}`} onClick={() => setArtStyle(id)} title={desc}
                  className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={artStyle === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#9a96ac] mt-1.5 text-center">{{
              photo: "True-to-life photographs for every scene",
              illustration: "Soft, warm hand-drawn storybook illustrations — lovely for kids' stories",
            }[artStyle]}</p>

            {/* Image Template — reuse the same characters across episodes */}
            <div className="mt-3 pt-3 border-t border-dashed border-[#dbe4d3]" data-testid="image-template">
              <div className="flex items-center justify-center gap-1.5 text-sm font-bold text-white">
                <ImagePlus size={15} style={{ color: "#5d8a52" }} /> Image Template <span className="text-[11px] font-semibold text-[#9a96ac]">(optional)</span>
              </div>
              <p className="text-[11px] text-[#9a96ac] mt-1 text-center px-2">
                Upload a picture of your characters to keep them looking the <b>same</b> in new videos.<br />
                Perfect for the next episode of a series.
              </p>
              {!imageTemplate ? (
                <label data-testid="image-template-upload" className="mt-2 flex items-center justify-center gap-2 cursor-pointer text-xs font-bold rounded-full px-4 py-2 mx-auto w-fit transition-colors"
                  style={{ background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                  <ImagePlus size={14} /> Upload character image
                  <input type="file" accept="image/*" className="hidden" onChange={onTemplatePick} />
                </label>
              ) : (
                <div className="mt-2 flex items-center justify-center gap-3">
                  <img src={imageTemplate} alt="character template" data-testid="image-template-preview"
                    className="w-14 h-14 rounded-lg object-cover border-2 border-[#5d8a52]" />
                  <button type="button" data-testid="image-template-remove" onClick={() => setImageTemplate(null)}
                    className="text-xs font-bold text-[#b23b3b] underline">Remove</button>
                </div>
              )}
            </div>
          </div>
          </>)}
        </div>
      </div>

      <div className="mt-4 sms-fade-up" style={{ animationDelay: ".1s" }} data-testid="host-env-lang-picker">
        <div className="sms-tweed rounded-2xl border-2 p-4" style={{ borderColor: "#5d8a52" }}>
          <div className="-mx-4 -mt-4 mb-3 py-2.5 rounded-t-2xl relative flex items-center justify-center" style={{ background: "#1e3aaa" }} data-testid="step-three-stripe">
            <span data-testid="step-three-title" className="font-extrabold text-xl tracking-wide text-white">STEP THREE</span>
            <button type="button" data-testid="step-three-collapse" onClick={() => toggleStep(setS3Open, "sms_step3_open")} className="absolute right-3 inline-flex items-center gap-1 text-xs font-bold text-white/85 hover:text-white transition-colors">
              {s3Open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}<span className="hidden sm:inline">{s3Open ? "Close" : "Open"}</span>
            </button>
          </div>
          {s3Open && (<>
          <div className="text-center font-bold text-lg mb-3">Host, Environment &amp; Language</div>

          <div className="max-w-[340px] mx-auto">
          {/* People in your video — pick No Host up front (it negates the options below) */}
          <div className="text-sm font-bold text-white mb-2">People in your video</div>
          <div className="grid grid-cols-3 gap-2" data-testid="people-count">
            {[[1, "1 person / host"], [2, "2 people"]].map(([v, l]) => {
              const on = peopleShots !== "none" && peopleCount === v;
              return (
                <button key={v} type="button" data-testid={`people-count-${v}`}
                  onClick={() => { setPeopleCount(v); if (peopleShots === "none") setPeopleShots("all"); }}
                  className="rounded-xl px-3 py-2.5 text-sm font-bold transition-colors leading-tight"
                  style={{ background: on ? "#1e3aaa" : "#000", color: "#fff", border: `1.5px solid ${on ? "#5b8cff" : "#5d8a52"}` }}>
                  {l}
                </button>
              );
            })}
            <button type="button" data-testid="people-shots-none"
              onClick={() => setPeopleShots("none")}
              className="rounded-xl px-3 py-2.5 text-sm font-bold transition-colors leading-tight"
              style={{ background: peopleShots === "none" ? "#1e3aaa" : "#000", color: "#fff", border: `1.5px solid ${peopleShots === "none" ? "#5b8cff" : "#5d8a52"}` }}>
              No Host
            </button>
          </div>
          {peopleShots === "none" && (
            <p className="text-[11px] text-[#9a96ac] mt-1.5" data-testid="no-host-note">
              No host — every photo is an object / detail shot of the topic, with no people at all.
            </p>
          )}

          {peopleShots !== "none" && peopleCount === 1 && (
            <div className="mt-2 space-y-2" data-testid="host-detail">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {[["", "Any"], ["woman", "Woman"], ["man", "Man"]].map(([id, label]) => (
                  <button key={id || "any"} type="button" data-testid={`host-gender-${id || "any"}`} onClick={() => setHostGender(id)}
                    className="text-xs font-bold px-3 py-1 rounded-full transition-colors"
                    style={hostGender === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="text-xs font-semibold text-[#a9b0c0]">Briefly describe your host and nationality</div>
              <input data-testid="scene-hint-input" value={sceneHint} onChange={(e) => setSceneHint(e.target.value)} maxLength={100}
                placeholder='e.g. "Friendly young Italian chef"'
                className="sms-input w-full px-3 py-2 text-sm" style={{ border: "2px solid #cfc6ea" }} />
            </div>
          )}
          {peopleShots !== "none" && peopleCount === 2 && (
            <div className="mt-2" data-testid="cast-detail">
              <input data-testid="cast-hint-input" value={castHint} onChange={(e) => setCastHint(e.target.value)} maxLength={100}
                placeholder='Who are they? — e.g. "mother + daughter"'
                className="sms-input w-full px-3 py-2 text-sm" style={{ border: "2px solid #cfc6ea" }} />
            </div>
          )}

          {/* How many of the 15 shots feature them — hidden entirely when No Host is chosen */}
          {peopleShots !== "none" && (
            <>
              <div className="text-sm font-bold text-white mt-4 mb-2">How many shots feature them?</div>
              <div className="grid grid-cols-2 gap-2" data-testid="people-shots">
                {[["all", "Every shot"], ["10", "10 shots"], ["5", "5 shots"]].map(([v, l]) => {
                  const on = peopleShots === v;
                  return (
                    <button key={v} type="button" data-testid={`people-shots-${v}`} onClick={() => setPeopleShots(v)}
                      className="rounded-xl px-3 py-2.5 text-sm font-bold transition-colors"
                      style={{ background: on ? "#1e3aaa" : "#000", color: "#fff", border: `1.5px solid ${on ? "#5b8cff" : "#5d8a52"}` }}>
                      {l}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#9a96ac] mt-1.5">
                {peopleShots === "all"
                  ? "People appear in all 15 steps, plus the intro and finish."
                  : `People appear in ${peopleShots} of the 15 steps — the rest become B-roll close-ups of the topic.`}
              </p>
            </>
          )}

          </div>

          <div className="mt-3 pt-3 border-t border-[#dbe4d3]" data-testid="create-location-section">
            <div className="text-lg font-bold text-center text-white mb-1">📍 Create Your Video Location</div>
            <p className="text-[11px] text-[#9a96ac] mb-3 text-center leading-relaxed">
              A few taps set the scene — our AI turns your picks into a full location. No typing needed (but you can add details at the end).
            </p>

            {/* 1 · Indoor / Outdoor */}
            <div className="mb-3">
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Indoor / Outdoor</div>
              <div className="flex flex-wrap gap-1.5">
                {[["", "Any"], ["indoor", "Indoor"], ["outdoor", "Outdoor"], ["both", "Both"]].map(([id, label]) => (
                  <button key={id || "any"} type="button" data-testid={`location-${id || "any"}`} onClick={() => setPlaceType(id)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={placeType === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 2 · Setting type */}
            <div className="mb-3">
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Setting</div>
              <div className="flex flex-wrap gap-1.5">
                {SETTING_TYPES.map(([id, label]) => (
                  <button key={id} type="button" data-testid={`setting-${id}`}
                    onClick={() => { setSettingType(settingType === id ? "" : id); setSpecificSpace(""); }}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={settingType === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 3 · Specific space (dynamic) */}
            {settingType && (SPECIFIC_SPACES[settingType] || []).length > 0 && (
              <div className="mb-3" data-testid="specific-space-group">
                <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Specific Space</div>
                <div className="flex flex-wrap gap-1.5">
                  {(SPECIFIC_SPACES[settingType] || []).map(([id, label]) => (
                    <button key={id} type="button" data-testid={`space-${id}`}
                      onClick={() => setSpecificSpace(specificSpace === id ? "" : id)}
                      className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                      style={specificSpace === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 4 · Surroundings */}
            <div className="mb-3">
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Surroundings</div>
              <div className="flex flex-wrap gap-1.5">
                {SURROUNDINGS.map(([id, label]) => (
                  <button key={id} type="button" data-testid={`surroundings-${id}`}
                    onClick={() => setSurroundings(surroundings === id ? "" : id)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={surroundings === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 5 · Weather */}
            <div className="mb-3">
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Weather</div>
              <div className="flex flex-wrap gap-1.5">
                {WEATHER.map(([id, label]) => (
                  <button key={id} type="button" data-testid={`weather-${id}`}
                    onClick={() => setWeather(weather === id ? "" : id)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={weather === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 6 · Lighting */}
            <div className="mb-3">
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Lighting</div>
              <div className="flex flex-wrap gap-1.5">
                {LIGHTING.map(([id, label]) => (
                  <button key={id} type="button" data-testid={`lighting-${id}`}
                    onClick={() => setLighting(lighting === id ? "" : id)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={lighting === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 7 · Time / Atmosphere */}
            <div className="mb-3">
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Time / Atmosphere</div>
              <div className="flex flex-wrap gap-1.5">
                {TIMES.map(([id, label]) => (
                  <button key={id || "any"} type="button" data-testid={`tod-${id || "any"}`} onClick={() => setTimeOfDay(id)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                    style={timeOfDay === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 7 · Optional free text */}
            <div>
              <div className="text-xs font-bold text-[#a9d79b] mb-1.5 uppercase tracking-wide">Enter Any Details <span className="text-[#7f9a74] normal-case">(optional)</span></div>
              <textarea
                data-testid="location-hint-input"
                value={locationHint}
                onChange={(e) => setLocationHint(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder={LOCATION_PLACEHOLDERS[locPh]}
                className="sms-input w-full px-4 py-3 text-sm resize-none leading-snug"
                style={{ border: "2px solid #1e3aaa" }}
              />
              <p className="text-[11px] text-[#9a96ac] mt-1.5 text-center">Skip everything and AI will pick a fitting location for your topic.</p>
              {composedScene && (
                <div className="mt-3 rounded-xl bg-black border border-[#5d8a52]/60 p-3" data-testid="scene-preview">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#a9d79b]">
                      <Sparkles size={12} /> Scene the AI will see
                    </span>
                    <span
                      data-testid="scene-preview-count"
                      className={`text-[10px] font-bold tabular-nums ${composedScene.length > 1800 ? "text-[#ff9b9b]" : composedScene.length > 1400 ? "text-[#ffd21e]" : "text-white/40"}`}
                    >
                      {composedScene.length.toLocaleString()} / 2,000
                    </span>
                  </div>
                  <p className="text-xs text-white/80 leading-snug italic" data-testid="scene-preview-text">{composedScene}</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-[#dbe4d3]" data-testid="language-row-home">
            <div className="text-lg font-bold text-white mb-2 text-center">Language</div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {[["en", "English"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"], ["it", "Italiano"], ["zh", "中文"], ["ja", "日本語"], ["pt", "Português"]].map(([id, label]) => (
                <button key={id} type="button" data-testid={`language-${id}`} onClick={() => setLanguage(id)}
                  className="text-xs font-bold px-3 py-1 rounded-full transition-colors"
                  style={language === id ? { background: "#1e3aaa", color: "#fff" } : { background: "#000", color: "#a9d79b", border: "1.5px solid #5d8a52" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          </>)}
        </div>
      </div>

      {msEnabled && (
        <div className="mt-6 sms-fade-up" data-testid="moviestar-home-card" style={{ animationDelay: ".12s" }}>
          <div className="sms-tweed rounded-2xl border-2 p-4" style={{ borderColor: "#5d8a52" }}>
            <div className="flex items-center justify-center gap-2 font-bold text-white">
              <Star size={18} className="text-[#5d8a52]" /> Be the Star of Your Video
              <span className="text-[10px] font-extrabold tracking-wide uppercase text-[#5d8a52] bg-white px-2 py-0.5 rounded-full">MovieStar</span>
            </div>
            <p className="text-sm text-[#a9b0c0] mt-1 leading-relaxed text-center">
              Add a clear, well-lit photo of your face (not your whole body) and your likeness appears in every step.
              <span className="font-semibold text-[#c3c8d4]"> We don't store your photo</span> — it's used once and discarded,
              so you'll add a fresh one each time you create a manual. And because it's AI, an exact match isn't guaranteed.
            </p>
            {!msPhoto ? (
              <div className="mt-3 flex justify-center">
                <label
                  data-testid="moviestar-upload-label"
                  className="inline-flex items-center gap-2 cursor-pointer text-sm font-bold text-white px-4 py-2.5 rounded-xl transition-colors"
                  style={{ background: "#4f7746" }}
                >
                  <Camera size={16} /> Upload your photo
                  <input data-testid="moviestar-upload-input" type="file" accept="image/*" className="hidden" onChange={onMsPhoto} />
                </label>
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-3">
                <img src={msPhoto} alt="Your photo" className="w-14 h-14 rounded-xl object-cover border-2 border-[#5d8a52]" data-testid="moviestar-photo-preview" />
                <div className="text-sm font-semibold text-[#16a34a]">Photo added ✓</div>
                <button
                  data-testid="moviestar-remove-photo"
                  onClick={() => setMsPhoto(null)}
                  className="ml-auto p-2 rounded-full text-[#9a96ac] hover:bg-white transition-colors"
                  aria-label="Remove photo"
                >
                  <X size={16} />
                </button>
              </div>
            )}
            {msPhoto && (
              <div className="mt-3" data-testid="moviestar-outfit-wrap">
                <label className="block text-xs font-bold text-[#c3c8d4] mb-1.5">
                  Outfit for continuity <span className="font-semibold text-[#9a96ac]">(optional, but recommended)</span>
                </label>
                <input
                  data-testid="moviestar-outfit-input"
                  type="text"
                  value={msOutfit}
                  onChange={(e) => setMsOutfit(e.target.value)}
                  maxLength={200}
                  placeholder='e.g. "navy blue apron over a white t-shirt"'
                  className="sms-input w-full px-4 py-2.5 text-sm"
                />
                <p className="text-[11px] text-[#9a96ac] mt-1.5 leading-relaxed">
                  Describe the clothes you're wearing and we'll keep the <span className="font-semibold text-[#5d8a52]">same outfit in every photo</span>. Leave blank and the AI picks (and it may change shot to shot).
                </p>
              </div>
            )}
            <div data-testid="moviestar-privacy-note" className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#5d8a52] bg-white/70 rounded-lg px-2.5 py-1.5 text-center">
              <Lock size={12} className="shrink-0" /> We don't store your photo — add a new one each time.
            </div>
          </div>
        </div>
      )}

      <button
        data-testid="generate-btn"
        onClick={generate}
        className="w-full text-white font-bold text-lg py-4 rounded-2xl mt-6 flex items-center justify-center gap-2 sms-fade-up transition-colors"
        style={{ animationDelay: ".15s", background: "linear-gradient(160deg,#ef4444,#dc2626)" }}
      >
        <AppIcon size={24} /> Generate Steps
      </button>
      </>
      )}

      <WhatIsModal open={showWhatIs} onClose={() => setShowWhatIs(false)} />
      <GoalGuideModal
        open={showGoalGuide}
        onClose={() => setShowGoalGuide(false)}
        onUseExample={(text) => setGoal(text)}
      />
      {showFreeSample && (
        <FreeSampleModal open onClose={() => setShowFreeSample(false)} initialGoal={goal} />
      )}
      </div>
    </div>
  );
}
